import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import SharedFolderPicker from 'components/@home/@settings/Tabs/Agents/SharedFolderPicker';
import FolderOutlineIcon from 'mdi-react/FolderOutlineIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const KnowledgeFolderSelector = ({
  folderId = '',
  onFolderIdChange,
  selectableFolders = [],
  showSave = false,
  onSave = null,
  saving = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingId, setPendingId] = useState(folderId);

  const label = useMemo(() => {
    if (!folderId) return null;
    const match = selectableFolders.find(f => String(f._id) === String(folderId));
    return match?.label || match?.name || null;
  }, [folderId, selectableFolders]);

  const openPicker = () => {
    setPendingId(folderId);
    setPickerOpen(true);
  };

  const handlePick = id => {
    onFolderIdChange?.(id);
    setPickerOpen(false);
  };

  return (
    <>
      <Stack spacing={1}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: '10px 12px',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.default',
          }}
        >
          <FolderOutlineIcon size={20} />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: theme => theme.typography.pxToRem(14),
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={label || undefined}
          >
            {label || I18n.t('AgenticChatFlow.KnowledgeFolderNotSelected')}
          </Typography>
          <Button variant="outlined" color="secondary" size="small" onClick={openPicker}>
            {folderId
              ? I18n.t('AgenticChatFlow.KnowledgeFolderChange')
              : I18n.t('AgenticChatFlow.KnowledgeFolderChoose')}
          </Button>
        </Box>
        {showSave && onSave && (
          <Button
            variant="contained"
            size="small"
            onClick={onSave}
            disabled={saving}
            sx={{ alignSelf: 'flex-start' }}
          >
            {I18n.t('AgenticChatFlow.SaveKnowledgeFolder')}
          </Button>
        )}
      </Stack>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{I18n.t('AgenticChatFlow.KnowledgeFolderPickerTitle')}</DialogTitle>
        <DialogContent>
          <SharedFolderPicker
            value={pendingId}
            onChange={handlePick}
            selectableFolders={selectableFolders}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPickerOpen(false)} color="inherit">
            {I18n.t('AgenticChatFlow.Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

KnowledgeFolderSelector.propTypes = {
  folderId: string,
  onFolderIdChange: func.isRequired,
  selectableFolders: arrayOf(object),
  showSave: bool,
  onSave: func,
  saving: bool,
};

export default KnowledgeFolderSelector;
